import { useState, useCallback, useMemo, useEffect } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { appendContextTag } from 'tripPlanner/utils/contextTag'
import { DRAWER_CONTEXT_PREFIX } from 'tripPlanner/utils/askAIContextMarkers'
import { buildDrawerAskAIContextMessage } from '../utils/drawerAskAIContext'

interface UseDrawerAskAIArgs {
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  messages: Array<MessageItem>
  isStreaming: boolean
  contextLabel: string
  isOpen: boolean
  /** Formatted drawer details to include as context when asking from a drawer. */
  contextDetails?: string
}

export interface DrawerSessionPair {
  question: MessageItem
  answer: MessageItem | undefined
}

interface UseDrawerAskAIReturn {
  handleSubmit: (message: string) => Promise<void>
  latestResponse: MessageItem | undefined
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
  hasActiveSession: boolean
}

export function useDrawerAskAI({
  submitMessage,
  messages,
  isStreaming,
  contextLabel,
  isOpen,
  contextDetails,
}: UseDrawerAskAIArgs): UseDrawerAskAIReturn {
  // Capture the current message count when the drawer opens.
  // Only messages added after this point belong to the current session.
  // messages.length is intentionally excluded from deps to avoid resetting mid-session.
  const [sessionStartIndex, setSessionStartIndex] = useState(() => messages.length)

  useEffect(() => {
    if (isOpen) {
      setSessionStartIndex(messages.length)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const contextMarker = `${DRAWER_CONTEXT_PREFIX}${contextLabel}`

  const handleSubmit = useCallback(async(message: string) => {
    const contextMessage = buildDrawerAskAIContextMessage({ contextLabel, contextDetails })
    const wrappedMessage = appendContextTag(message, contextMessage, contextLabel)
    await submitMessage(wrappedMessage)
  }, [submitMessage, contextLabel, contextDetails])

  const { latestResponse, sessionHistory, isResponseStreaming, isWaitingForResponse } = useMemo(() => {
    const sessionMessages = messages.slice(sessionStartIndex)

    const pairs: Array<DrawerSessionPair> = []

    for (let i = 0; i < sessionMessages.length; i++) {
      const msg = sessionMessages[i]
      if (msg.role === 'user' && msg.rawContent?.includes(contextMarker)) {
        const nextMsg = sessionMessages[i + 1]
        const answer = nextMsg?.role === 'assistant' ? nextMsg : undefined
        pairs.push({ question: msg, answer })
      }
    }

    const latestPair = pairs.length > 0 ? pairs[pairs.length - 1] : undefined
    const latestResponse = latestPair?.answer

    // The response is streaming when the global stream is active and the latest response
    // is the last message in the full array (i.e. the stream is for our message)
    const lastMessage = messages[messages.length - 1]
    const isResponseStreaming = !!(isStreaming && lastMessage?.id === latestResponse?.id)

    // Waiting state: we submitted but the assistant message hasn't appeared yet.
    // Also verify our question is the tail of the global array, so a main-chat
    // stream doesn't trigger a false loading state in the drawer.
    const isWaitingForResponse = !!(isStreaming && latestPair?.answer === undefined && lastMessage?.id === latestPair?.question.id)

    return { latestResponse, sessionHistory: pairs, isResponseStreaming, isWaitingForResponse }
  }, [messages, sessionStartIndex, contextMarker, isStreaming])

  return {
    handleSubmit,
    latestResponse,
    sessionHistory,
    isResponseStreaming,
    isWaitingForResponse,
    hasActiveSession: sessionHistory.length > 0,
  }
}
